import {
  createMemoryHistory as createMemoryHistory5,
  MemoryHistory as MemoryHistory5,
  Path as Path5,
  To
} from 'history5';
import { createBrowserHistory as createBrowserHistory4, History as History4, LocationDescriptor } from 'history';

interface History5Compat extends MemoryHistory5 {
  _push: MemoryHistory5['push'];
  _replace: MemoryHistory5['replace'];
  _go: MemoryHistory5['go'];
  _back: MemoryHistory5['back'];
  _forward: MemoryHistory5['forward'];
}

interface History4Compat extends History4 {
  _push: History4['push'];
  _replace: History4['replace'];
  _go: History4['go'];
  _goBack: History4['goBack'];
  _goForward: History4['goForward'];
}

const isExternalURL = (url: string) => {
  try {
    return new URL(url).origin !== location.origin;
  } catch (e) {
    return false;
  }
};

const createHistoryInstances = () => {
  const history5 = (createMemoryHistory5() as unknown) as History5Compat;
  const history4 = (createBrowserHistory4() as unknown) as History4Compat;

  history4._push = history4.push;
  history4._replace = history4.replace;
  history4._go = history4.go;
  history4._goBack = history4.goBack;
  history4._goForward = history4.goForward;

  // Monkey patch all of the necessary v4 functions to also call the v5 counterparts
  history4.push = (path: string | LocationDescriptor, state?: unknown) => {
    if (typeof path === 'string') {
      history5._push(path, state);
      history4._push(path, state);
    } else {
      history5._push(path as Path5, state);
      history4._push(path);
    }
  };
  history4.replace = (path: string | LocationDescriptor, state?: unknown) => {
    if (typeof path === 'string') {
      history5._replace(path, state);
      history4._replace(path, state);
    } else {
      history5._replace(path as Path5, state);
      history4._replace(path);
    }
  };
  history4.go = (n: number) => {
    history5._go(n);
    history4._go(n);
  };
  history4.goBack = () => {
    history5._back();
    history4._goBack();
  };
  history4.goForward = () => {
    history5._forward();
    history4._goForward();
  };

  history5._push = history5.push;
  history5._replace = history5.replace;
  history5._go = history5.go;
  history5._back = history5.back;
  history5._forward = history5.forward;

  // Monkey patch all of the necessary v5 functions to also call the v4 counterparts
  history5.push = (to: To, state?: unknown) => {
    if (typeof to === 'string') {
      if (isExternalURL(to)) {
        location.href = to;
      } else {
        history4._push(to, state);
      }
    } else {
      history4._push({ ...to, state } as LocationDescriptor);
    }
    history5._push(to, state);
  };
  history5.replace = (to: To, state?: unknown) => {
    if (typeof to === 'string') {
      if (to.indexOf('error?code=') !== -1) {
        return; // ignore error pages from global nav
      }
      history4._replace(to, state);
    } else {
      history4._replace({ ...to, state } as LocationDescriptor);
    }
    history5._replace(to, state);
  };
  history5.go = (delta: number) => {
    history4._go(delta);
    history5._go(delta);
  };
  history5.back = () => {
    history4._goBack();
    history5._back();
  };
  history5.forward = () => {
    history4._goForward();
    history5._forward();
  };

  return { history4, history5 };
};

export const { history4, history5 } = createHistoryInstances();
